import { Drawer, Group, Paper, ScrollArea, SegmentedControl, Stack, Text } from "@mantine/core"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import { ListState } from "../ui"
import { useGovernanceConfigQuery } from "../settings/hooks"
import { CreateInboundOrderDialog } from "./CreateInboundOrderDialog"
import { useInboundOrdersQuery } from "./hooks"
import { useInboundActions } from "./inbound-actions"
import { InboundOrderDetail } from "./InboundOrderDetail"
import { InboundOrdersTable } from "./InboundOrdersTable"

// PICKUP_COMPLETED is never written by the backend (contract §2 O134), so it has no tab.
// Same order as INBOUND_STATUS (de-order-service); PICKUP_COMPLETED = «Цуглуулсан» (canvas Ord_Inbound).
const STATUS_VALUES = ["DRAFT", "APPROVED", "DISPATCHED_PICKUP", "PICKUP_COMPLETED", "IN_TRANSIT", "AT_DESTINATION", "RECEIVED", "CANCELLED"] as const
const SOURCE_VALUES = ["INTERNAL_WAREHOUSE", "EXTERNAL_PARTY"] as const
const ALL = "ALL"
const PAGE_SIZE = 20

type Props = Readonly<{
  // When set, the source-type filter is hidden and locked to this type.
  lockedSourceType?: (typeof SOURCE_VALUES)[number] | null
  /** The «Шинэ» button lives in the page header, so the page owns the create dialog state. */
  createOpened: boolean
  onCreateClose: () => void
}>

export function InboundOrders({ lockedSourceType = null, createOpened, onCreateClose }: Props) {
  const { t } = useTranslation(["inbound", "common"])
  const { stateOf } = useInboundActions()
  const [searchParams, setSearchParams] = useSearchParams()
  const statusParam = (STATUS_VALUES as readonly string[]).includes(searchParams.get("status") ?? "") ? (searchParams.get("status") as string) : ALL
  const sourceRaw = lockedSourceType ?? searchParams.get("sourceType")
  const sourceParam = (SOURCE_VALUES as readonly string[]).includes(sourceRaw ?? "") ? (sourceRaw as string) : ALL

  const [page, setPage] = useState(1)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { data, isLoading, error, mutate } = useInboundOrdersQuery({
    status: statusParam === ALL ? null : statusParam,
    sourceType: sourceParam === ALL ? null : sourceParam,
    page,
    pageSize: PAGE_SIZE,
  })
  const rows = data?.rows ?? []
  const total = data?.count ?? 0
  const filtered = statusParam !== ALL || sourceParam !== ALL

  // §27.15: hide a source option when its inbound flow is disabled by governance config.
  // Missing config (loading / no BIZ_GOV_VIEW) shows everything.
  const { data: govConfig } = useGovernanceConfigQuery()
  const sourceOptions = [
    { value: ALL, label: t("inbound:list.allSources") },
    ...SOURCE_VALUES.filter((v) => {
      if (!govConfig) return true
      if (v === "INTERNAL_WAREHOUSE") return !!govConfig.inboundFlow?.crossWarehouseSourcing?.enabled
      return !!govConfig.inboundFlow?.externalPickupEnabled
    }).map((v) => ({ value: v, label: t(`inbound:sourceType.${v}`) })),
  ]

  const setParam = (key: string, value: string) => {
    setPage(1)
    setSelectedId(null)
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      if (value === ALL) params.delete(key)
      else params.set(key, value)
      return params
    })
  }

  const clearFilters = () => {
    setPage(1)
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      params.delete("status")
      params.delete("sourceType")
      return params
    })
  }

  const viewState = stateOf("view", {})
  const statusData = [{ value: ALL, label: t("inbound:list.allStatus") }, ...STATUS_VALUES.map((v) => ({ value: v, label: t(`inbound:status.${v}`) }))]

  let body
  if (error) {
    body = <ListState state="error" description={t("inbound:list.loadError")} onRetry={() => mutate()} />
  } else if (isLoading && !data) {
    body = <ListState state="loading" columns={6} rows={4} />
  } else if (rows.length === 0) {
    body = filtered ? (
      <ListState state="noResults" action={{ onClick: clearFilters }} />
    ) : (
      <ListState state="empty" title={t("inbound:list.emptyRows")} />
    )
  } else {
    body = (
      <InboundOrdersTable
        rows={rows}
        selectedId={selectedId}
        onSelect={(id) => setSelectedId((prev) => (prev === id ? null : id))}
        viewState={viewState}
        total={total}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={(next) => {
          setPage(next)
          setSelectedId(null)
        }}
      />
    )
  }

  return (
    <Stack gap="md">
      <ScrollArea type="auto" offsetScrollbars>
        <SegmentedControl value={statusParam} onChange={(v) => setParam("status", v)} data={statusData} />
      </ScrollArea>

      <Paper withBorder radius="md" p={0}>
        <Group justify="space-between" p="md" wrap="wrap">
          <Text fz={14}>{t("inbound:list.subtitle", { count: total })}</Text>
          {!lockedSourceType && sourceOptions.length > 2 && <SegmentedControl size="sm" value={sourceParam} onChange={(v) => setParam("sourceType", v)} data={sourceOptions} />}
        </Group>
        {body}
      </Paper>

      <Drawer opened={!!selectedId} onClose={() => setSelectedId(null)} position="right" size="xl" padding={0} withCloseButton={false} overlayProps={{ backgroundOpacity: 0.4, blur: 2 }} keepMounted={false}>
        {selectedId && <InboundOrderDetail inboundOrderId={selectedId} onClose={() => setSelectedId(null)} />}
      </Drawer>

      <CreateInboundOrderDialog opened={createOpened} onClose={onCreateClose} onCreated={(id) => setSelectedId(id)} />
    </Stack>
  )
}
